/**
 * PL/SQL `CREATE` 문에서 **무엇을 만드는지**(유형 · 소유자 · 이름)를 읽는다.
 *
 * PL/SQL 편집기의 컴파일이 이 결과로 (1) 원문을 DB 에 보낼지 정하고 (2) 컴파일 오류를
 * 어느 객체에서 찾을지 정한다. **여기가 틀리면 에러가 나도 「성공」이 뜬다.**
 *
 * 실측(2026-09-28, 교육DB 19c): 목록에서 연 객체의 소스는 `DBMS_METADATA.GET_DDL` 이 주는
 *   `CREATE OR REPLACE EDITIONABLE PROCEDURE "EDU"."LSQL_DDL_TMP" AS …`
 * 꼴인데, 예전 정규식은 `EDITIONABLE` 도 `"소유자"."이름"` 도 몰라서 인식에 실패했다. 그러면
 * 화면은 편집한 원문 대신 `ALTER … COMPILE` 로 **DB 에 저장된 옛 소스**를 다시 컴파일했고,
 * 옛 소스는 멀쩡하니 「컴파일 성공 — 오류 없음」이 떴다. 편집한 내용은 DB 에 가지도 않았다.
 * 새로 타이핑한 소스(EDITIONABLE·따옴표 없음)는 됐기 때문에 "가끔 그렇다" 로 보였다.
 *
 * 이름 규칙은 Oracle 그대로다 — 따옴표 없는 이름은 대문자로 접히고, 따옴표 친 이름은 적힌
 * 그대로다(`"myProc"` 은 `MYPROC` 이 아니다). 소유자를 안 적었으면 `owner: null` 이고,
 * 그때 객체는 **세션의 현재 스키마**에 만들어진다(화면의 스키마 칸이 아니다).
 *
 * @param {string} text
 * @returns {{ type: string, owner: string|null, name: string } | null}
 */
const HEAD_RE = new RegExp(
    '^\\s*CREATE\\s+(?:OR\\s+REPLACE\\s+)?'
    // 12c+ 의 GET_DDL 은 EDITIONABLE 을 붙여 준다. 트리거는 EDITIONING 이 아니라 이것뿐이다.
    + '(?:(?:NON)?EDITIONABLE\\s+)?'
    + '(PACKAGE\\s+BODY|PACKAGE|PROCEDURE|FUNCTION|TRIGGER|TYPE\\s+BODY|TYPE)\\s+',
    'i',
);

// 따옴표 친 식별자 | 맨 식별자 — 그 뒤에 `.이름` 이 오면 앞의 것이 소유자다
//
// 맨 식별자는 **글자(유니코드)** 로 시작한다. Oracle 은 DB 문자셋이 지원하면 따옴표 없는 이름에도
// 한글 등을 허용한다 — 예전의 `[A-Za-z][A-Za-z0-9_$#]*` 는 `MIG.PRC_MIG_적재실행` 을
// `MIG.PRC_MIG_` 로 잘라 읽어 엉뚱한 객체에서 컴파일 결과를 찾았다(신고 2026-11-02).
// 같은 글자 집합을 config/dbQueries 의 PLSQL_NAME_RE(조회 문장의 이름 검사)도 쓴다 — 한쪽만 넓히면 읽고도 조회에서 거부된다.
const BARE_IDENT_SRC = '\\p{L}[\\p{L}\\p{N}\\p{M}_$#]*';
const IDENT = `(?:"([^"]+)"|(${BARE_IDENT_SRC}))`;
const NAME_RE = new RegExp(`^${IDENT}(?:\\s*\\.\\s*${IDENT})?`, 'u');

const fold = (quoted, bare) => (quoted != null ? quoted : String(bare).toUpperCase());

export function parsePlsqlCreate(text) {
    const src = String(text || '');
    const head = HEAD_RE.exec(src);
    if (!head) return null;
    const type = head[1].toUpperCase().replace(/\s+/g, ' ');
    const rest = src.slice(head.index + head[0].length);
    const m = NAME_RE.exec(rest);
    if (!m) return null;
    const first = fold(m[1], m[2]);
    if (m[3] != null || m[4] != null) {
        return { type, owner: first, name: fold(m[3], m[4]) };
    }
    return { type, owner: null, name: first };
}
